import React, { useState, useCallback } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { useEditShowroom } from "hooks/showroomHook"
import ButtonTooltip from "components/Common/ButtonTooltip"

export default function ShowroomProprietorUpdate({ showroom = null }) {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  const { mutateAsync: editShowroom, isPending: showroomEditLoading } =
    useEditShowroom()

  const validationSchema = Yup.object({
    proprietor_name: Yup.string().nullable(),
    proprietor_mobile: Yup.string().nullable(),
    proprietor_address: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async values => {
      try {
        const res = await editShowroom({ id: showroom?.id, ...values })
        setModal(false)
        toast.success(res?.message ?? "Proprietor details updated successfully.")
      } catch (error) {
        console.error("Failed to update proprietor details:", error)
        toast.error("Failed to update proprietor details.")
      }
    },
    [editShowroom, showroom]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      proprietor_name: showroom?.proprietor_name || "",
      proprietor_mobile: showroom?.proprietor_mobile || "",
      proprietor_address: showroom?.proprietor_address || "",
    },
    validationSchema,
    onSubmit,
  })

  return (
    <>
      {/* ✅ Edit button */}
      <div className="d-flex justify-content-end">
        <ButtonTooltip
          type="edit"
          onClick={toggle}
          btnId="edit-showroom-proprietor"
          btnName="Edit Proprietor Details"
        />
      </div>

      {/* ✅ Modal */}
      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>Edit Proprietor Details</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <Row>
              <Col md={6}>
                <Label>Proprietor Name</Label>
                <Input
                  name="proprietor_name"
                  value={validation.values.proprietor_name}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.proprietor_name}
                />
                <FormFeedback>
                  {validation.errors.proprietor_name}
                </FormFeedback>
              </Col>

              <Col md={6}>
                <Label>Proprietor Mobile</Label>
                <Input
                  name="proprietor_mobile"
                  value={validation.values.proprietor_mobile}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.proprietor_mobile}
                />
                <FormFeedback>
                  {validation.errors.proprietor_mobile}
                </FormFeedback>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>Proprietor Address</Label>
                <Input
                  type="textarea"
                  rows={3}
                  name="proprietor_address"
                  value={validation.values.proprietor_address}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.proprietor_address}
                />
                <FormFeedback>
                  {validation.errors.proprietor_address}
                </FormFeedback>
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button
                type="submit"
                color="success"
                disabled={showroomEditLoading}
              >
                Save Changes
                {showroomEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
